import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useSocketContext } from './SocketContext'
import { useSelector } from 'react-redux'

const OnlineStatusContext = createContext({
  isUserOnline: () => false,
  onlineUsers: new Set(),
})

export function OnlineStatusProvider({ children }) {
  const [onlineUsers, setOnlineUsers] = useState(new Set())
  const { socket, isConnected } = useSocketContext()
  const currentUser = useSelector((state) => state.user?.user)

  useEffect(() => {
    // Ne rien faire si socket ou currentUser n'est pas disponible
    if (!socket || !currentUser?.id) {
      //console.warn('Socket ou currentUser non disponible:', !!socket, currentUser?.id);
      return
    }

    // Gestionnaire pour les changements de statut
    const handleStatusChange = (data) => {
      if (!data || typeof data.userId === 'undefined') {
        console.warn('Format invalide pour user_status_change:', data)
        return
      }

      const userId = Number(data.userId)

      setOnlineUsers((prev) => {
        const newSet = new Set(Array.from(prev).map((id) => Number(id)))
        if (data.online) {
          newSet.add(userId)
        } else {
          newSet.delete(userId)
        }
        return newSet
      })
    }

    // Gestionnaire pour l'état initial
    const handleInitialStatus = (onlineUserIds) => {
      if (!Array.isArray(onlineUserIds)) {
        console.warn('Format invalide pour initial_status:', onlineUserIds)
        return
      }

      const userIds = onlineUserIds.map((id) => Number(id))
      setOnlineUsers(new Set(userIds))
    }

    // Fonction pour émettre le statut
    const emitOnlineStatus = () => {
      if (!socket || !socket.connected) {
        console.warn("Socket non connecté, impossible d'émettre le statut")
        return
      }

      const numUserId = Number(currentUser.id)

      socket.emit('user_status_change', {
        userId: numUserId,
        online: true,
      })

      // Demander la liste complète
      socket.emit('get_online_users')
    }

    // Configuration des écouteurs d'événements
    socket.on('user_status_change', handleStatusChange)
    socket.on('initial_status', handleInitialStatus)

    // Gestion des reconnexions/connexions
    socket.on('connect', () => {
      emitOnlineStatus()
    })

    // Émission immédiate si déjà connecté
    if (socket.connected) {
      emitOnlineStatus()
    }

    // Nettoyage lors du démontage
    return () => {
      socket.off('user_status_change', handleStatusChange)
      socket.off('initial_status', handleInitialStatus)
      socket.off('connect')

      if (socket.connected && currentUser?.id) {
        socket.emit('user_status_change', {
          userId: Number(currentUser.id),
          online: false,
        })
      }
    }
  }, [socket, currentUser])

  // Création de la valeur du contexte
  const value = useMemo(() => {
    // S'assurer que l'utilisateur courant est considéré comme en ligne localement
    const userOnlineSet = new Set(Array.from(onlineUsers).map((id) => Number(id)))

    if (currentUser?.id) {
      userOnlineSet.add(Number(currentUser.id))
    }

    return {
      isUserOnline: (userId) => {
        if (!userId) return false
        const numUserId = Number(userId)
        const isOnline = userOnlineSet.has(numUserId)
        return isOnline
      },
      onlineUsers: userOnlineSet,
    }
  }, [onlineUsers, currentUser])

  return <OnlineStatusContext.Provider value={value}>{children}</OnlineStatusContext.Provider>
}

export function useOnlineStatus() {
  return useContext(OnlineStatusContext)
}
